import { useEffect, useRef, useState } from "react";
import { Upload, Images } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import ImageCard from "./ImageCard";
import StorageImagePicker from "./StorageImagePicker";

// Toujours Supabase Storage directement dans l'admin (bypass Cloudflare CDN)
const STORAGE_BASE = "https://bhvsqiffenuabtwekgsj.supabase.co/storage/v1/object/public/images";
function storageUrl(path: string) {
  return `${STORAGE_BASE}/${path}`;
}

interface SiteImage {
  id: string;
  section: string;
  key: string;
  url: string;
  alt: string;
  order: number;
}

interface ImageSectionEditorProps {
  section: string;
  label: string;
  maxImages?: number;
}

export default function ImageSectionEditor({ section, maxImages }: ImageSectionEditorProps) {
  const [images, setImages] = useState<SiteImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadImages = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("site_images")
      .select("id, section, key, url, alt, order")
      .eq("section", section)
      .order("order");
    setImages((data as SiteImage[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { loadImages(); }, [section]);

  const nextOrder = () => (images.length > 0 ? Math.max(...images.map((i) => i.order)) + 1 : 1);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const path = `uploads/${Date.now()}-${file.name}`;
    const { error: storageError } = await supabase.storage.from("images").upload(path, file);
    if (storageError) {
      alert(`Erreur upload storage : ${storageError.message}`);
    } else {
      const { error: dbError } = await supabase.from("site_images").insert({ section, key: Date.now().toString(), url: path, alt: "", order: nextOrder() });
      if (dbError) {
        alert(`Erreur ajout en base : ${dbError.message}`);
      } else {
        await loadImages();
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePickFromLibrary = async (paths: string[]) => {
    let order = nextOrder();
    const rows = paths.map((path) => ({ section, key: `${Date.now()}-${path}`, url: path, alt: "", order: order++ }));
    await supabase.from("site_images").insert(rows);
    await loadImages();
  };

  const moveUp = async (index: number) => {
    if (index === 0) return;
    const a = images[index], b = images[index - 1];
    await supabase.from("site_images").update({ order: b.order }).eq("id", a.id);
    await supabase.from("site_images").update({ order: a.order }).eq("id", b.id);
    await loadImages();
  };

  const moveDown = async (index: number) => {
    if (index === images.length - 1) return;
    const a = images[index], b = images[index + 1];
    await supabase.from("site_images").update({ order: b.order }).eq("id", a.id);
    await supabase.from("site_images").update({ order: a.order }).eq("id", b.id);
    await loadImages();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("site_images").delete().eq("id", id);
    await loadImages();
  };

  const handleAltChange = async (id: string, alt: string) => {
    await supabase.from("site_images").update({ alt }).eq("id", id);
  };

  const canUpload = maxImages === undefined || images.length < maxImages;

  return (
    <div>
      {pickerOpen && (
        <StorageImagePicker
          onSelect={handlePickFromLibrary}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="text-sm text-gray-500">
          {images.length} image{images.length !== 1 ? "s" : ""}
          {maxImages !== undefined ? ` / ${maxImages}` : ""}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => setPickerOpen(true)}
            disabled={!canUpload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Images size={14} /> Bibliothèque
          </button>
          <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white cursor-pointer transition-opacity ${canUpload && !uploading ? "opacity-100" : "opacity-40 cursor-not-allowed"}`} style={{ backgroundColor: "#4a7c59" }}>
            <Upload size={14} />
            {uploading ? "Envoi…" : "Uploader"}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" disabled={!canUpload || uploading} onChange={handleUpload} />
          </label>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[1, 2, 3].map((n) => <Skeleton key={n} className="h-52 rounded-lg" />)}
        </div>
      ) : images.length === 0 ? (
        <p className="text-sm text-gray-400 italic">Aucune image pour cette section.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((img, index) => (
            <ImageCard
              key={img.id}
              url={storageUrl(img.url)}
              alt={img.alt}
              order={img.order}
              isFirst={index === 0}
              isLast={index === images.length - 1}
              onMoveUp={() => moveUp(index)}
              onMoveDown={() => moveDown(index)}
              onDelete={() => handleDelete(img.id)}
              onAltChange={(alt) => handleAltChange(img.id, alt)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
